// ─── ReportScheduleListModal ──────────────────────────────────────────────────
// "Report List" — one row per UserReport in the current project. When a
// report has an enabled ReportSchedule, the row shows the next-run
// timestamp and a Disable link that toggles the schedule off. Remove
// deletes the underlying UserReport (schedules cascade via the FK).
//
// Fetches both listReports() and listSchedules() so the merge happens
// client-side — the two data sets stay independent and neither endpoint
// has to know about the other.

import { useEffect, useMemo, useState } from 'react'
import { X, Loader, AlertCircle } from 'lucide-react'
import {
  listReports, deleteReport,
  listSchedules, updateSchedule,
} from '../../lib/reportApi'
import useAuthStore from '../../store/auth-store'
import { resolveIanaTz } from '../../lib/tz'

function fmtNextRun(s, customerTz) {
  if (!s?.next_run_at) return ''
  // Server stores `next_run_at` as UTC "YYYY-MM-DD HH:MM:SS". Format in
  // the user's *profile timezone* so the label matches their working
  // hours regardless of which device / browser they're on.
  const d = new Date(s.next_run_at.replace(' ', 'T') + 'Z')
  if (isNaN(d.getTime())) return s.next_run_at
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: customerTz,
      month: 'long', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
      timeZoneName: 'short',
    }).format(d)
  } catch { return d.toString() }
}

export default function ReportScheduleListModal({ projectId, onClose }) {
  const [reports,   setReports]   = useState([])
  const [schedules, setSchedules] = useState([])
  const [loading,   setLoading]   = useState(false)
  const [error,     setError]     = useState(null)
  const [busy,      setBusy]      = useState({})  // { [key]: 'toggle' | 'remove' }
  const rawTz      = useAuthStore(s => s.user?.timezone)
  const customerTz = resolveIanaTz(rawTz)

  const load = async () => {
    if (!projectId) return
    setLoading(true); setError(null)
    try {
      const [rs, ss] = await Promise.all([
        listReports(projectId).catch(() => []),
        listSchedules(projectId).catch(() => []),
      ])
      setReports(Array.isArray(rs) ? rs : [])
      setSchedules(Array.isArray(ss) ? ss : [])
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load reports')
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { load() /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [projectId])

  // Merge: one row per report; pick the *primary* schedule to show its
  // next-run + Disable link. A report can have multiple schedules across
  // transports; we surface the first enabled one, falling back to any
  // schedule at all. Users manage per-transport schedules from the
  // Share/Export modals themselves.
  const rows = useMemo(() => {
    const byReport = new Map()
    for (const s of schedules) {
      const arr = byReport.get(s.report_id) || []
      arr.push(s)
      byReport.set(s.report_id, arr)
    }
    return reports.map((r) => {
      const list = byReport.get(r.id) || []
      const primary = list.find((s) => s.enabled) || list[0] || null
      return {
        report:   r,
        schedule: primary,
      }
    })
  }, [reports, schedules])

  const toggleSchedule = async (schedule) => {
    if (!schedule) return
    const k = `sched-${schedule.id}`
    setBusy((b) => ({ ...b, [k]: 'toggle' }))
    try {
      await updateSchedule(schedule.id, { enabled: !schedule.enabled })
      await load()
    } catch (e) {
      alert(e?.response?.data?.message || e?.message || 'Could not update schedule')
    } finally {
      setBusy((b) => { const n = { ...b }; delete n[k]; return n })
    }
  }

  const removeReport = async (report) => {
    if (!window.confirm(`Remove "${report.name}"? This deletes the report and any schedules attached to it.`)) return
    const k = `rep-${report.id}`
    setBusy((b) => ({ ...b, [k]: 'remove' }))
    try {
      await deleteReport(report.id)
      setReports((rs) => rs.filter((r) => r.id !== report.id))
      // Any schedules FK'd to this report cascade on the server side.
      setSchedules((ss) => ss.filter((s) => s.report_id !== report.id))
    } catch (e) {
      alert(e?.response?.data?.message || e?.message || 'Could not remove report')
    } finally {
      setBusy((b) => { const n = { ...b }; delete n[k]; return n })
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
         onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-800">Report List</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 pt-3 pb-2">
          <div className="text-sm font-semibold text-gray-800">Report Name</div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 pb-4">
          {error && (
            <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" /> {error}
            </div>
          )}
          {loading ? (
            <div className="py-8 text-center text-gray-500 text-sm">
              <Loader className="inline w-4 h-4 animate-spin mr-1" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="py-6 text-center text-sm text-gray-500 border border-gray-200 rounded">
              No reports in this project yet.
            </div>
          ) : (
            <div className="border border-gray-200 rounded divide-y divide-gray-200">
              {rows.map(({ report, schedule }) => {
                const kSched = schedule ? `sched-${schedule.id}` : null
                const kRep   = `rep-${report.id}`
                const isRemoving = busy[kRep] === 'remove'
                const isToggling = kSched && busy[kSched] === 'toggle'
                return (
                  <div key={report.id} className="grid grid-cols-[1fr,auto,auto] items-center gap-4 px-3 py-2.5 text-sm">
                    <div className="min-w-0">
                      <div className="text-gray-800 truncate">{report.name}</div>
                    </div>
                    <div className="text-sm text-gray-600 whitespace-nowrap">
                      {schedule?.enabled && schedule.next_run_at
                        ? `Next Run at ${fmtNextRun(schedule, customerTz)}`
                        : ''}
                    </div>
                    <div className="flex items-center gap-4 shrink-0">
                      {schedule ? (
                        <button
                          type="button"
                          onClick={() => toggleSchedule(schedule)}
                          disabled={isToggling}
                          className="underline text-gray-700 hover:text-[#358ECD] text-sm disabled:opacity-50"
                        >
                          {schedule.enabled ? 'Disable' : 'Enable'}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => removeReport(report)}
                        disabled={isRemoving}
                        className="underline text-red-600 hover:text-red-700 text-sm disabled:opacity-50"
                      >
                        {isRemoving ? 'Removing…' : 'Remove'}
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <div className="px-6 pt-2 pb-4">
          <button
            type="button"
            onClick={onClose}
            className="w-full py-2.5 bg-[#358ECD] hover:bg-[#2A78B0] text-white text-sm font-bold tracking-wider rounded"
          >
            CLOSE
          </button>
        </div>
      </div>
    </div>
  )
}
